// Personal — pantalla del rediseño del directivo (brief §4.7, antes "Personas").
//
// Responde "¿cómo está mi gente?" con una tabla de OPERACIÓN por persona:
//   - Ronda 1 (2026-07-05): filtros consolidados (turno SOLO en preset Hoy —
//     los equipos rotan y en un rango no distingue nada), fuera Reconocimiento
//     y Dónde apoyar, toggle Líderes|Supervisores dentro de la tabla.
//   - Ronda 2 (2026-07-05): fuera Puntualidad y Fuera de turno de la tabla
//     (siguen como KPI); SUPERVISORES con la misma información de operación
//     que los líderes (producción de sus turnos); cumplimiento con el
//     semáforo compartido de la plataforma (verde ≥90 / ámbar 70-89 / rojo
//     <70); utilidad negativa en rojo; nota del asterisco ($ parcial por SKUs
//     sin precio); renombrada a "Personal".
//   - Click en una persona → perfil C7b (con el shell del directivo).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DirectivoPersonas } from 'schemas';
import { Card } from '../../components/ui/card';
import { InfoHint } from '../../components/ui/info-hint';
import { KpiCard } from '../../components/ui/kpi-card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoPersonas } from '../../lib/api/queries';
import { semaforoCumplimiento, toneTextClass, type Tone } from '../../lib/semaforo';
import { cn } from '../../lib/utils';
import { BarraPeriodo, usePeriodo } from './lib/periodo';
import { copM, miles } from './lib/formato';

type RolSel = 'lideres' | 'supervisores';
type TurnoSel = 'todos' | '1' | '2' | '3';

/** Porcentaje YA calculado → entero es-CO ("93 %"). */
function pctFmt(pct: number): string {
  return `${Math.round(pct)} %`;
}

function horasFmt(h: number): string {
  return `${h.toLocaleString('es-CO', { maximumFractionDigits: 1 })} h`;
}

export function PersonasPage() {
  const [rolSel, setRolSel] = useState<RolSel>('lideres');
  const [plantaSel, setPlantaSel] = useState<string>('todas');
  const [turnoSel, setTurnoSel] = useState<TurnoSel>('todos');
  const [areaSel, setAreaSel] = useState<string>('todas');
  // Turno solo aplica en "Hoy": los equipos ROTAN de turno entre días — en un
  // rango el filtro mezclaría personas, no compararía turnos.
  const ctrl = usePeriodo(
    (p) => {
      if (p !== 'hoy') setTurnoSel('todos');
    },
    'semana',
    { url: true },
  );
  const { periodo } = ctrl;

  const personas = useDirectivoPersonas({
    desde: periodo.desde,
    hasta: periodo.hasta,
    plantaId: plantaSel === 'todas' ? null : plantaSel,
    turno: turnoSel === 'todos' ? null : Number(turnoSel),
    areaId: areaSel === 'todas' ? null : areaSel,
  });

  const data = personas.data;
  const multiPlanta = (data?.plantas.length ?? 0) > 1;
  // Opciones de área: las de la planta filtrada; con "Todas" se desambigua
  // con el código de planta (hay nombres repetidos entre plantas).
  const areaOpciones = useMemo(() => {
    if (!data) return [];
    const codigo = new Map(data.plantas.map((p) => [p.id, p.codigo]));
    return data.areas
      .filter((a) => plantaSel === 'todas' || a.planta_id === plantaSel)
      .map((a) => ({
        value: a.id,
        label:
          plantaSel === 'todas' && multiPlanta
            ? `${a.nombre} · ${codigo.get(a.planta_id) ?? ''}`
            : a.nombre,
      }));
  }, [data, plantaSel, multiPlanta]);

  return (
    <DirectivoLayout>
      {/* ── Barra de filtros (una sola franja, como el resto del mundo) ── */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <BarraPeriodo ctrl={ctrl} />
        <div className="flex-1" />
        {multiPlanta && data ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Planta
            </span>
            <SegmentedControl
              value={plantaSel}
              onChange={(v) => {
                setPlantaSel(v);
                setAreaSel('todas');
              }}
              size="sm"
              options={[
                { value: 'todas', label: `Todas (${data.plantas.length})` },
                ...data.plantas.map((p) => ({ value: p.id, label: p.nombre })),
              ]}
            />
          </div>
        ) : null}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-[11px] text-fg-tertiary">Personal / {periodo.label}</p>
        <div className="flex-1" />
        {ctrl.preset === 'hoy' ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Turno
            </span>
            <SegmentedControl
              value={turnoSel}
              onChange={(v) => setTurnoSel(v as TurnoSel)}
              size="sm"
              options={[
                { value: 'todos', label: 'Todos' },
                { value: '1', label: 'T1' },
                { value: '2', label: 'T2' },
                { value: '3', label: 'T3' },
              ]}
            />
          </div>
        ) : null}
        {areaOpciones.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Área
            </span>
            <Select
              value={areaSel}
              onChange={(e) => setAreaSel(e.target.value)}
              className="h-8 w-auto min-w-36 text-[12px]"
            >
              <option value="todas">Todas</option>
              {areaOpciones.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </div>

      {personas.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : personas.isError || !data ? (
        <Card className="p-6 text-sm text-fg-secondary">
          No se pudo cargar el análisis de personal. Intente de nuevo en unos segundos.
        </Card>
      ) : (
        <PersonasContenido
          data={data}
          rolSel={rolSel}
          onRol={setRolSel}
          periodoLabel={periodo.label}
          periodoRango={{ desde: periodo.desde, hasta: periodo.hasta }}
        />
      )}
    </DirectivoLayout>
  );
}

function PersonasContenido({
  data,
  rolSel,
  onRol,
  periodoLabel,
  periodoRango,
}: {
  data: DirectivoPersonas;
  rolSel: RolSel;
  onRol: (r: RolSel) => void;
  periodoLabel: string;
  periodoRango: { desde: string; hasta: string };
}) {
  const k = data.kpis;
  const plantaCob = k.cobertura.por_planta
    .filter((p) => p.esperados > 0)
    .map((p) => `${p.codigo} ${p.pct !== null ? pctFmt(p.pct) : '—'}`)
    .join(' · ');

  // Ambos roles comparten la MISMA tabla de operación (ronda 2).
  const filas: FilaOperacion[] = useMemo(
    () =>
      rolSel === 'lideres'
        ? data.lideres.map((l) => ({
            user_id: l.user_id,
            nombre: `${l.nombre} ${l.apellido}`,
            tag: l.planta_codigo,
            detalle: l.lineas,
            unidades: l.unidades,
            meta: l.meta_capacidad_unds,
            cumplimiento: l.cumplimiento_meta_pct,
            horas: l.horas,
            valor: l.valor_cop,
            utilidad: l.utilidad_cop,
            personal: l.personal_promedio,
            sin_precio: l.skus_sin_precio,
            // Programado sin capturar: la fila existe por programacion_lideres.
            sin_actividad: l.bloques_capturados === 0,
          }))
        : data.supervisores.map((s) => ({
            user_id: s.user_id,
            nombre: `${s.nombre} ${s.apellido}`,
            tag: null,
            detalle: s.planta_codigos,
            unidades: s.unidades,
            meta: s.meta_capacidad_unds,
            cumplimiento: s.cumplimiento_meta_pct,
            horas: s.horas,
            valor: s.valor_cop,
            utilidad: s.utilidad_cop,
            personal: s.personal_promedio,
            sin_precio: s.skus_sin_precio,
            sin_actividad: s.unidades === 0 && s.horas === 0,
            validacion: {
              validados: s.validaciones,
              pendientes: s.pendientes_validacion,
              pendientes_paq: s.pendientes_paq,
            },
          })),
    [data, rolSel],
  );

  return (
    <>
      {/* ── KPIs del período ── */}
      <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Card className="p-4">
          <KpiCard
            className="px-0"
            label="Cobertura de captura"
            hint="Bloques-hora capturados sobre los esperados según la programación de líderes. El día en curso solo cuenta las horas ya transcurridas del turno."
            value={k.cobertura.pct !== null ? pctFmt(k.cobertura.pct) : '—'}
            sub={
              k.cobertura.esperados > 0
                ? `${miles(k.cobertura.capturados)} de ${miles(Math.round(k.cobertura.esperados))} bloques esperados${plantaCob ? ` · ${plantaCob}` : ''}`
                : 'Sin programación de líderes en el período'
            }
          />
        </Card>
        <Card className="p-4">
          <KpiCard
            className="px-0"
            label="Puntualidad de captura"
            hint="Mediana de minutos entre el cierre de cada hora y su captura. Capturar antes del cierre cuenta como 0."
            value={
              k.puntualidad_mediana_min !== null
                ? String(Math.round(k.puntualidad_mediana_min))
                : '—'
            }
            valueSuffix={k.puntualidad_mediana_min !== null ? 'min' : undefined}
            sub="mediana tras el cierre de cada hora"
          />
        </Card>
        <Card className="p-4">
          <KpiCard
            className="px-0"
            label="Validación"
            hint="Bloques capturados que ya pasaron por validación del supervisor."
            value={k.validacion.pct !== null ? pctFmt(k.validacion.pct) : '—'}
            sub={
              k.validacion.pendientes > 0
                ? `${miles(k.validacion.pendientes)} pendientes de supervisor`
                : 'sin pendientes de supervisor'
            }
            subTone={k.validacion.pendientes > 0 ? 'warn' : 'neutral'}
          />
        </Card>
        <Card className="p-4">
          <KpiCard
            className="px-0"
            label="Disciplina del dato"
            hint="Capturas fuera de la ventana del turno y capturas delegadas (las hizo alguien distinto del líder asignado)."
            value={`${miles(k.disciplina.fuera_turno)}`}
            valueSuffix={`fuera de turno · ${miles(k.disciplina.delegadas)} delegadas`}
            sub={
              k.disciplina.pct_bloques !== null
                ? `${k.disciplina.pct_bloques.toLocaleString('es-CO', { maximumFractionDigits: 1 })}% de los bloques del período`
                : 'sin bloques en el período'
            }
          />
        </Card>
      </div>

      <TablaOperacion
        titulo={`${rolSel === 'lideres' ? 'Líderes' : 'Supervisores'} · ${periodoLabel}`}
        colDetalle={rolSel === 'lideres' ? 'Línea(s)' : 'Plantas'}
        filas={filas}
        conCostos={data.puede_ver_costos}
        rolSel={rolSel}
        onRol={onRol}
        periodo={periodoRango}
        notaVacia={
          rolSel === 'lideres'
            ? 'Sin capturas ni programación de líderes en el período filtrado.'
            : 'Sin programación ni acciones de supervisores en el período filtrado.'
        }
      />
    </>
  );
}

// ── Tabla de operación (compartida por líderes y supervisores) ──

interface FilaOperacion {
  user_id: string;
  nombre: string;
  tag: string | null;
  detalle: string[];
  unidades: number;
  meta: number;
  cumplimiento: number | null;
  horas: number;
  valor: number | null;
  utilidad: number | null;
  personal: number | null;
  sin_precio: number;
  /** Programado/asignado en el período pero sin capturas — la fila no está "en cero", está SIN DATO. */
  sin_actividad: boolean;
  /**
   * Solo supervisores: validación de capturas con la MISMA fórmula que el
   * Análisis de personal del coordinador (bloques de sus turnos programados en
   * sus plantas; pendientes = `validado_at IS NULL`).
   */
  validacion?: { validados: number; pendientes: number; pendientes_paq: number };
}

type SortKey =
  | 'nombre'
  | 'unidades'
  | 'meta'
  | 'cumplimiento'
  | 'horas'
  | 'valor'
  | 'utilidad'
  | 'personal'
  | 'validados'
  | 'pendientes';

const TONE_BG: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
  neutral: 'bg-fg-tertiary',
};

/** Semáforo compartido de la plataforma: barra + % con color (como C7b). */
function CumplimientoCell({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-fg-tertiary">—</span>;
  const tone = semaforoCumplimiento(pct);
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="h-1 w-10 shrink-0 overflow-hidden rounded-full bg-border">
        <span
          className={cn('block h-full rounded-full', TONE_BG[tone])}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </span>
      <span className={cn('whitespace-nowrap font-medium', toneTextClass(tone))}>
        {pctFmt(pct)}
      </span>
    </span>
  );
}

/** Detalle sin engordar la fila: 1 código + "+N" con la lista en el title. */
function DetalleCell({ codigos }: { codigos: string[] }) {
  if (codigos.length === 0) return <>—</>;
  const visibles = codigos.slice(0, 1).join(', ');
  const resto = codigos.length - 1;
  return (
    <span className="whitespace-nowrap" title={codigos.join(', ')}>
      {visibles}
      {resto > 0 ? <span className="text-fg-tertiary"> +{resto}</span> : null}
    </span>
  );
}

function TablaOperacion({
  titulo,
  colDetalle,
  filas,
  conCostos,
  rolSel,
  onRol,
  notaVacia,
  periodo,
}: {
  titulo: string;
  colDetalle: string;
  filas: FilaOperacion[];
  conCostos: boolean;
  /** Período en pantalla: el perfil individual lo recibe por `?desde&hasta` (como desde C7). */
  periodo: { desde: string; hasta: string };
  rolSel: RolSel;
  onRol: (r: RolSel) => void;
  notaVacia: string;
}) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortKey>('unidades');
  const [asc, setAsc] = useState(false);

  const ordenados = useMemo(() => {
    const dir = asc ? 1 : -1;
    const val = (f: FilaOperacion): number | string => {
      switch (sort) {
        case 'nombre':
          return f.nombre.toLowerCase();
        case 'unidades':
          return f.unidades;
        case 'meta':
          return f.meta;
        case 'cumplimiento':
          return f.cumplimiento ?? -1;
        case 'horas':
          return f.horas;
        case 'valor':
          return f.valor ?? -1;
        case 'utilidad':
          return f.utilidad ?? Number.MIN_SAFE_INTEGER;
        case 'personal':
          return f.personal ?? -1;
        case 'validados':
          return f.validacion?.validados ?? -1;
        case 'pendientes':
          return f.validacion?.pendientes ?? -1;
      }
    };
    return [...filas].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return (Number(va) - Number(vb)) * dir;
    });
  }, [filas, sort, asc]);

  const cambiarSort = (s: SortKey, ascDefault: boolean) => {
    if (sort === s) setAsc((v) => !v);
    else {
      setSort(s);
      setAsc(ascDefault);
    }
  };

  const hayParciales = filas.some((f) => f.sin_precio > 0 && f.valor !== null);
  const conValidacion = rolSel === 'supervisores';

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-3 px-4 pt-4">
        <h2 className="text-[15px] font-medium text-fg" style={{ letterSpacing: '-0.2px' }}>
          {titulo}
        </h2>
        <SegmentedControl
          value={rolSel}
          onChange={(v) => onRol(v as RolSel)}
          size="sm"
          options={[
            { value: 'lideres', label: 'Líderes' },
            { value: 'supervisores', label: 'Supervisores' },
          ]}
        />
        <div className="flex-1" />
        <p className="font-mono text-[10px] text-fg-tertiary">
          orden: {asc ? 'ascendente' : 'descendente'} · click en columna para ordenar
        </p>
      </div>
      {filas.length === 0 ? (
        <p className="px-4 pb-5 pt-3 text-sm text-fg-secondary">{notaVacia}</p>
      ) : (
        <>
          <div className="mt-3 max-h-[68vh] overflow-auto">
            <table
              className="w-full border-collapse text-[12.5px]"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">
                  <Th onClick={() => cambiarSort('nombre', true)} activo={sort === 'nombre'}>
                    Persona
                  </Th>
                  <Th>{colDetalle}</Th>
                  <Th onClick={() => cambiarSort('unidades', false)} activo={sort === 'unidades'}>
                    Producción
                  </Th>
                  <Th onClick={() => cambiarSort('meta', false)} activo={sort === 'meta'}>
                    <span className="inline-flex items-center gap-1">
                      Meta capacidad
                      <InfoHint label="Meta capacidad instalada">
                        Capacidad estándar acumulada de las horas capturadas (SKU × línea).
                      </InfoHint>
                    </span>
                  </Th>
                  <Th
                    onClick={() => cambiarSort('cumplimiento', false)}
                    activo={sort === 'cumplimiento'}
                  >
                    Cumplimiento
                  </Th>
                  <Th onClick={() => cambiarSort('horas', false)} activo={sort === 'horas'}>
                    Horas
                  </Th>
                  {conValidacion ? (
                    <>
                      <Th
                        onClick={() => cambiarSort('validados', false)}
                        activo={sort === 'validados'}
                      >
                        <span className="inline-flex items-center gap-1">
                          Validados
                          <InfoHint label="Bloques validados">
                            Bloques de producción del período seleccionado que fueron validados por
                            este supervisor. Misma cuenta que el Análisis de personal del
                            coordinador.
                          </InfoHint>
                        </span>
                      </Th>
                      <Th
                        onClick={() => cambiarSort('pendientes', false)}
                        activo={sort === 'pendientes'}
                      >
                        <span className="inline-flex items-center gap-1">
                          Pend.
                          <InfoHint label="Pendientes de validar">
                            Bloques de sus turnos programados que siguen sin validar. No entran a la
                            foto del turno, a la utilidad validada ni al corte de facturación.
                          </InfoHint>
                        </span>
                      </Th>
                    </>
                  ) : null}
                  {conCostos ? (
                    <>
                      <Th onClick={() => cambiarSort('valor', false)} activo={sort === 'valor'}>
                        <span className="inline-flex items-center gap-1">
                          Valor producido
                          <InfoHint label="Valor producido">
                            Unidades × precio vigente del SKU, sobre producción validada por
                            supervisión (los bloques sin validar no suman). Con asterisco (*) el
                            valor es PARCIAL: capturó SKUs que aún no tienen precio cargado.
                          </InfoHint>
                        </span>
                      </Th>
                      <Th
                        onClick={() => cambiarSort('utilidad', false)}
                        activo={sort === 'utilidad'}
                      >
                        <span className="inline-flex items-center gap-1">
                          Utilidad
                          <InfoHint label="Utilidad estimada">
                            Valor producido (+ insumos cobrados en tiendas) menos el costo de mano
                            de obra de sus horas, a precios vigentes, sobre producción validada por
                            supervisión.
                          </InfoHint>
                        </span>
                      </Th>
                    </>
                  ) : null}
                  <Th onClick={() => cambiarSort('personal', false)} activo={sort === 'personal'}>
                    <span className="inline-flex items-center gap-1">
                      Personal prom.
                      <InfoHint label="Personal promedio">
                        Promedio de operarios con los que trabajó por hora capturada.
                      </InfoHint>
                    </span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {ordenados.map((f) => (
                  <tr
                    key={f.user_id}
                    onClick={() =>
                      navigate(
                        `/coordinador/personal/${f.user_id}?desde=${periodo.desde}&hasta=${periodo.hasta}`,
                      )
                    }
                    className="cursor-pointer border-t border-border/60 hover:bg-surface-elevated"
                  >
                    <td className="px-4 py-2.5 font-medium text-fg">
                      <span className="block whitespace-nowrap">{f.nombre}</span>
                      {f.tag ? (
                        <span className="block font-mono text-[10px] font-normal text-fg-tertiary">
                          {f.tag}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-24 px-4 py-2.5 font-mono text-[11px] text-fg-secondary">
                      <DetalleCell codigos={f.detalle} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                      {f.sin_actividad ? (
                        <span
                          className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-fg-tertiary"
                          title="Estuvo programado en el período pero no registró capturas"
                        >
                          Sin capturas
                        </span>
                      ) : (
                        miles(f.unidades)
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                      {f.meta > 0 ? miles(f.meta) : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <CumplimientoCell pct={f.cumplimiento} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                      {horasFmt(f.horas)}
                    </td>
                    {conValidacion ? (
                      <>
                        <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                          {f.validacion ? miles(f.validacion.validados) : '—'}
                        </td>
                        <td
                          className={cn(
                            'whitespace-nowrap px-4 py-2.5',
                            f.validacion && f.validacion.pendientes > 0
                              ? 'font-medium text-warn'
                              : 'text-fg-secondary',
                          )}
                          title={
                            f.validacion && f.validacion.pendientes > 0
                              ? `${miles(f.validacion.pendientes_paq)} paquetes sin validar`
                              : undefined
                          }
                        >
                          {f.validacion ? miles(f.validacion.pendientes) : '—'}
                        </td>
                      </>
                    ) : null}
                    {conCostos ? (
                      <>
                        <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                          {f.valor !== null ? (
                            <span
                              title={
                                f.sin_precio > 0
                                  ? `${f.sin_precio} SKUs sin precio — valor parcial`
                                  : undefined
                              }
                            >
                              {copM(f.valor)}
                              {f.sin_precio > 0 ? '*' : ''}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          className={cn(
                            'whitespace-nowrap px-4 py-2.5',
                            f.utilidad !== null && f.utilidad < 0
                              ? 'font-medium text-crit'
                              : 'text-fg-secondary',
                          )}
                        >
                          {f.utilidad !== null ? copM(f.utilidad) : '—'}
                        </td>
                      </>
                    ) : null}
                    <td className="whitespace-nowrap px-4 py-2.5 text-fg-secondary">
                      {f.personal !== null
                        ? f.personal.toLocaleString('es-CO', { maximumFractionDigits: 1 })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <TotalesFoot filas={filas} conCostos={conCostos} conValidacion={conValidacion} />
            </table>
          </div>
          {hayParciales && conCostos ? (
            <p className="border-t border-border/60 px-4 py-2.5 text-[11px] text-fg-tertiary">
              * Valor parcial: la persona capturó SKUs que aún no tienen precio cargado — esas
              unidades entran como $0 hasta que se carguen los precios.
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/** Totales del período: sumas directas; cumplimiento = Σprod/Σmeta con el
 *  semáforo; personal = promedio PONDERADO por horas (el simple mentiría). */
function TotalesFoot({
  filas,
  conCostos,
  conValidacion,
}: {
  filas: FilaOperacion[];
  conCostos: boolean;
  conValidacion: boolean;
}) {
  const unidades = filas.reduce((s, f) => s + f.unidades, 0);
  const validados = filas.reduce((s, f) => s + (f.validacion?.validados ?? 0), 0);
  const pendientes = filas.reduce((s, f) => s + (f.validacion?.pendientes ?? 0), 0);
  const meta = filas.reduce((s, f) => s + f.meta, 0);
  const horas = filas.reduce((s, f) => s + f.horas, 0);
  const cumplimiento = meta > 0 ? (unidades / meta) * 100 : null;
  const valor = filas.reduce((s, f) => s + (f.valor ?? 0), 0);
  const utilidad = filas.reduce((s, f) => s + (f.utilidad ?? 0), 0);
  const parcial = filas.some((f) => f.sin_precio > 0);
  const horasConPersonal = filas.reduce((s, f) => s + (f.personal !== null ? f.horas : 0), 0);
  const personal =
    horasConPersonal > 0
      ? filas.reduce((s, f) => s + (f.personal ?? 0) * (f.personal !== null ? f.horas : 0), 0) /
        horasConPersonal
      : null;

  const celda = 'whitespace-nowrap px-4 py-2.5 font-medium text-fg';
  return (
    <tfoot>
      <tr className="sticky bottom-0 z-10 bg-surface-elevated shadow-[0_-1px_0_var(--color-border-stronger)]">
        <td className={celda}>Total · {filas.length} personas</td>
        <td className="px-4 py-2.5" />
        <td className={celda}>{miles(unidades)}</td>
        <td className={celda}>{meta > 0 ? miles(meta) : '—'}</td>
        <td className="px-4 py-2.5">
          <CumplimientoCell pct={cumplimiento !== null ? Number(cumplimiento.toFixed(1)) : null} />
        </td>
        <td className={celda}>{horasFmt(horas)}</td>
        {conValidacion ? (
          <>
            <td className={celda}>{miles(validados)}</td>
            <td className={cn(celda, pendientes > 0 ? 'text-warn' : '')}>{miles(pendientes)}</td>
          </>
        ) : null}
        {conCostos ? (
          <>
            <td className={celda}>
              {copM(valor)}
              {parcial ? '*' : ''}
            </td>
            <td className={cn(celda, utilidad < 0 ? 'text-crit' : '')}>{copM(utilidad)}</td>
          </>
        ) : null}
        <td className={celda}>
          {personal !== null ? personal.toLocaleString('es-CO', { maximumFractionDigits: 1 }) : '—'}
        </td>
      </tr>
    </tfoot>
  );
}

function Th({
  children,
  onClick,
  activo,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  activo?: boolean;
}) {
  return (
    <th
      onClick={onClick}
      className={cn(
        'sticky top-0 z-10 whitespace-nowrap bg-surface-elevated px-4 py-2 font-medium shadow-[0_1px_0_var(--color-border-stronger)]',
        onClick ? 'cursor-pointer select-none hover:text-fg' : '',
        activo ? 'text-fg' : '',
      )}
    >
      {children}
    </th>
  );
}
